import { List, Map } from "immutable"
import slugify from "slugify"
import { loadingAsyncState } from "widgets-for-react"
import { fixMediaUrl, processRichText } from "../../Shared/api/drupal/drupalApi"
import { Teaser } from "../../Shared/api/drupal/drupalTypes"
import { getTeasers } from "../../Shared/api/elasticsearch/elasticsearchApi"
import {
  ParsedParagraphActivityCluster,
  ParsedParagraphBooks, ParsedParagraphCaroussel, ParsedParagraphCollection, ParsedParagraphCollectionSearchBar, ParsedParagraphContentGrid,
  ParsedParagraphFaqCollection,
  ParsedParagraphFeaturedContent, ParsedParagraphFormstackBlock, ParsedParagraphGridBlock, ParsedParagraphHighlight,
  ParsedParagraphImages,
  ParsedParagraphInlineMedia,
  ParsedParagraphKnop,
  ParsedParagraphMedia, ParsedParagraphPageGrid, ParsedParagraphPanorama, ParsedParagraphQuote, ParsedParagraphRecentNews, ParsedParagraphSubscriptionBlock, ParsedParagraphText,
  ParsedParagraphVacancyOverview
} from "../../Shared/api/drupal/types"
import { ActivityClusterBlockProps } from "../custom_components/components/activity_cluster_block"
import { AsyncBlockProps } from "../custom_components/components/async_block"
import { Block } from "../custom_components/components/block_renderer"
import { BookProps, BooksBlockProps } from "../custom_components/components/books_block"
import { ButtonBlockProps } from "../custom_components/components/button_block"
import { CarouselContentGridBlockProps, CarouselContentGridItem } from "../custom_components/components/carousel_content_grid_block"
import {
  CollectionBlockProps,
  CollectionGroup,
  CollectionGroupItem,
  CollectionGroupItemIconList,
  collectionGroupItemIconList
} from "../custom_components/components/collection_block"
import { CollectionSearchBarProps } from "../custom_components/components/collection_search_bar"
import { ContentGridBlockProps, ContentGridItem } from "../custom_components/components/content_grid_block"
import { FaqBlockProps } from "../custom_components/components/faq_block"
import { FeaturedContentBlockProps } from "../custom_components/components/featured_content_block"
import { loadFormstackForm } from "../custom_components/components/formstack/formstack_api"
import { FormstackBlockProps } from "../custom_components/components/formstack/formstack_block"
import { GridBlockProps, GridItem } from "../custom_components/components/grid_block"
import { HighlightBlockProps } from "../custom_components/components/highlight_block"
import { ImagesBlockProps } from "../custom_components/components/images_block"
import { ImageBlockProps } from "../custom_components/components/image_block"
import { PanoramaBlockProps } from "../custom_components/components/panorama/PanoramaState"
import { QuoteBlockProps } from "../custom_components/components/quote_block"
import { RecentNewsBlockProps } from "../custom_components/components/recentNewsBlock/recentNewsBlockState"
import { RichTextBlockProps } from "../custom_components/components/rich_text_block"
import { SubscriptionItem, SubscriptionsBlockProps } from "../custom_components/components/subscriptions_block"
import { VacancyOverviewBlockProps } from "../custom_components/components/vacancy_overview_block"
import { VideoBlockProps } from "../custom_components/components/video_block"

export const richTextBlock: (p: ParsedParagraphText) => RichTextBlockProps = p => ({
  kind: "richText",
  title: p.field_title,
  html: p.field_rich_text ? processRichText(p.field_rich_text) : "",
  primaryActionTitle: p.field_primary_action ? p.field_primary_action.title : "",
  primaryActionUrl: p.field_primary_action ? p.field_primary_action.url : "",
  secondaryActionTitle: p.field_secondary_action ? p.field_secondary_action.title : "",
  secondaryActionUrl: p.field_secondary_action ? p.field_secondary_action.url : "",
  media: p.field_inline_media
    ? {
      media: mediaBlock(p.field_inline_media),
      position: p.field_inline_media.field_position == "text_left" ? "text_left" : "text_right",
    }
    : "none",
})

export type MediaBlockProps = VideoBlockProps & { kind: "video" } | ImageBlockProps & { kind: "image" }
export const mediaBlock: (p: ParsedParagraphMedia | ParsedParagraphInlineMedia) => MediaBlockProps = p => {
  return p.field_media.bundle == "video"
    ? {
      kind: "video",
      videoUrl: p.field_media.field_media_video_embed_field.iframe_url,
      videoName: p.field_media.label,
      autoplay: false,
      controls: true,
      info: true,
    }
    : {
      kind: "image",
      imageUrlSmall: fixMediaUrl(p.field_media.field_media_image.image.style_urls["391w"]),
      imageUrl: fixMediaUrl(p.field_media.field_media_image.image.style_urls["782w"]),
      imageUrlLarge: fixMediaUrl(p.field_media.field_media_image.image.style_urls["1564w"]),
      imageAlt: p.field_media.field_media_image.alt,
    }
}

export const faqBlock: (p: ParsedParagraphFaqCollection) => FaqBlockProps = p => ({
  kind: "faq",
  title: p.field_faq_title,
  entries: p.field_faq_item.map(i => ({ question: i.label, answer: i.field_answer ? processRichText(i.field_answer) : "" })).toList(),
})

export const highlightBlock: (p: ParsedParagraphHighlight) => HighlightBlockProps = p => ({
  kind: "highlight",
  title: p.field_title,
  subtitle: p.field_subtitle,
  content: p.field_rich_text,
})

export const buttonBlock: (p: ParsedParagraphKnop) => ButtonBlockProps = p => ({
  kind: "knop",
  caption: p.field_link.title,
  href: p.field_link.url,
})

export const imagesBlock: (p: ParsedParagraphImages) => ImagesBlockProps = p => ({
  kind: "images",
  images: p.field_images.reduce(
    (l, i) =>
      l.push({
        imageAlt: i.field_media_image.alt,
        imageUrlSmall: fixMediaUrl(i.field_media_image.image.style_urls["420x320_cropped"]),
        imageUrl: fixMediaUrl(i.field_media_image.image.style_urls["840x640_cropped"]),
        imageUrlLarge: fixMediaUrl(i.field_media_image.image.style_urls["840x640_cropped"]),
        imageUrlExtraLarge: fixMediaUrl(i.field_media_image.image.url),
      }),
    List<ImageBlockProps>()
  ),
})

export const vacancyOverviewBlock: (p: ParsedParagraphVacancyOverview) => VacancyOverviewBlockProps = p => ({
  kind: "vacancy_overview",
  title: p.field_title,
  html: p.field_rich_text,
  category_id: p.field_category.id,
})

export const booksOverviewBlock: (p: ParsedParagraphBooks) => BooksBlockProps = p => ({
  kind: "books",
  books: p.field_books
    .map<BookProps>(b => ({
      title: b.field_title,
      author: b.field_author,
      imageUrl: fixMediaUrl(b.field_image.field_media_image.image.style_urls["195x195"]),
      imageUrlLarge: fixMediaUrl(b.field_image.field_media_image.image.style_urls["390x390"]),
      imageAlt: b.field_image.field_media_image.alt,
      url: b.field_link.url
    }))
    .toList(),
  title: p.field_title,
  text: p.field_text,
})

export const collectionBlock: (p: ParsedParagraphCollection) => CollectionBlockProps = p => ({
  kind: "collection",
  title: p.field_title,
  introduction: p.field_rich_text,
  collectionGroups: p.field_collection_groups
    .map<CollectionGroup>(group => ({
      title: group.field_title,
      slug: slugify(group.field_title, { lower: true }),
      collectionItems: group.field_collection_items
        .map<CollectionGroupItem>(item => {
          let icon: CollectionGroupItemIconList = collectionGroupItemIconList
            .filter(v => v === item.field_icon)
            .toList()
          return {
            title: item.field_title,
            subtitle: item.field_subtitle,
            text: item.field_text,
            link: item.field_link.url,
            icon: !icon.isEmpty() ? icon.first() : "none",
          }
        })
        .toList(),
    }))
    .toList(),
})

export const featuredContentBlock: (p: ParsedParagraphFeaturedContent) => FeaturedContentBlockProps = p => ({
  kind: "featured_content",
  title: p.field_title,
  html: p.field_rich_text,
  imageUrl: fixMediaUrl(p.field_image.field_media_image.image.style_urls["448x448"]),
  imageUrlLarge: fixMediaUrl(p.field_image.field_media_image.image.style_urls["896x896"]),
  imageAlt: p.field_image.field_media_image.alt,
  linkTitle: p.field_link.title,
  linkUrl: p.field_link.url,
})

export const activityClusterBlock: (p: ParsedParagraphActivityCluster) => ActivityClusterBlockProps = p => ({
  kind: "activity_cluster",
  title: p.field_title,
  description: p.field_text,
  slug: "",
  activitySummariesIds: p.field_product_db_ids,
})

export const subscriptionsBlock: (p: ParsedParagraphSubscriptionBlock) => SubscriptionsBlockProps = p => ({
  kind: "subscription_block",
  items: p.field_subscription_item.reduce(
    (l, i) =>
      l.push({
        title: i.field_title,
        price: i.field_price,
        sticker: i.field_sticker ? i.field_sticker : "",
        extra: i.field_text ? i.field_text : "",
        materials: i.field_materials,
        services: i.field_services ? i.field_services : "",
        linkTitle: i.field_link.title,
        linkUrl: i.field_link.url,
        isYouth: i.field_is_youth
      }),
    List<SubscriptionItem>()
  ),
})

export const contentGridBlock: (p: ParsedParagraphContentGrid) => ContentGridBlockProps = p => ({
  kind: "content_grid",
  items: p.field_content_grid_items.reduce(
    (l, i) =>
      l.push({
        title: i.field_title,
        subtitle: i.field_subtitle ? i.field_subtitle : "",
        label: i.field_label ? i.field_label : "",
        imageAlt: i.bundle == "content_grid_item" ? i.field_image.field_media_image.alt : "",
        imageUrl: i.bundle == "content_grid_item" ? fixMediaUrl(i.field_image.field_media_image.image.style_urls["420x320_cropped"]) : "",
        imageUrlLarge: i.bundle == "content_grid_item" ? fixMediaUrl(i.field_image.field_media_image.image.style_urls["840x640_cropped"]) : "",
        videoUrl: i.bundle == "content_grid_video" ? i.field_video.iframe_url : "",
        linkUrl: i.field_link ? i.field_link.url : "",
      }),
    List<ContentGridItem>()
  ),
})

export const recentNewsBlock: (p: ParsedParagraphRecentNews) => RecentNewsBlockProps = p => ({
  kind: "recent_news",
  title: p.field_title,
  count: p.field_count,
  linkTitle: p.field_link && p.field_link.title,
  linkUrl: p.field_link && p.field_link.url
})

export const carouselContentGridBlock: (p: ParsedParagraphCaroussel) => CarouselContentGridBlockProps = p => ({
  kind: "carousel_content_grid",
  title: p.field_title,
  items: p.field_content_grid_items.reduce(
    (l, i) =>
      l.push({
        title: i.field_title,
        subtitle: i.field_subtitle ? i.field_subtitle : "",
        label: i.field_label ? i.field_label : "",
        imageAlt: i.bundle == "content_grid_item" ? i.field_image.field_media_image.alt : "",
        imageUrl: i.bundle == "content_grid_item" ? fixMediaUrl(i.field_image.field_media_image.image.style_urls["420x320_cropped"]) : "",
        imageUrlLarge: i.bundle == "content_grid_item" ? fixMediaUrl(i.field_image.field_media_image.image.style_urls["840x640_cropped"]) : "",
        videoUrl: i.bundle == "content_grid_video" ? i.field_video.iframe_url : "",
        linkUrl: i.field_link.url
      }),
    List<CarouselContentGridItem>()
  ),
  primaryActionTitle: p.field_primary_action ? p.field_primary_action.title : "",
  primaryActionUrl: p.field_primary_action ? p.field_primary_action.url : "",
})

export const pageGridBlock: (p: ParsedParagraphPageGrid) => AsyncBlockProps<Map<string, Teaser>> = p => ({
  kind: "async_block",
  promise: () => getTeasers(p.field_page_grid_items.map(i => i.id.toString()).toArray()),
  block: teasers => ({
    kind: "content_grid",
    title: p.field_title,
    items: teasers.map<ContentGridItem>(t => ({
      title: t.title,
      linkUrl: t.url,
      imageAlt: t.imageAlt,
      imageUrl: t.imageUrl,
      imageUrlLarge: t.imageUrlLarge,
      subtitle: t.description,
    })).toList(),
    primaryActionTitle: p.field_primary_action ? p.field_primary_action.title : undefined,
    primaryActionUrl: p.field_primary_action ? p.field_primary_action.url : undefined,
  })
})

export const quoteBlock: (p: ParsedParagraphQuote) => QuoteBlockProps = p => {
  return ({
    kind: "quote",
    quote: p.field_quote_text,
    source: p.field_quote_source && p.field_quote_source != "" ? p.field_quote_source : undefined,
  })
}

export const collectionSearchBarBlock: (p: ParsedParagraphCollectionSearchBar) => CollectionSearchBarProps = p => {
  return ({
    kind: "collection_search_bar"
  })
}

export const gridBlock: (p: ParsedParagraphGridBlock) => GridBlockProps = p => {
  return ({
    kind: "grid_block",
    title: p.field_title,
    row_count: p.field_row_count ? p.field_row_count : 3,
    items: p.field_grid_items.reduce(
      (l, i) =>
        l.push({
          title: i.field_title,
          imageAlt: i.field_media.field_media_image.alt,
          imageUrl: fixMediaUrl(i.field_media.field_media_image.image.style_urls['420x320_cropped']),
          imageUrlLarge: fixMediaUrl(i.field_media.field_media_image.image.style_urls['840x640_cropped']),
          intro: i.field_text,
          linkUrl: i.field_link.url
        }),
      List<GridItem>()
    )
  })
}

export const formstackBlock: (p: ParsedParagraphFormstackBlock) => FormstackBlockProps = p => {
  return ({
    kind: p.bundle,
    formId: p.field_formstack_form_id,
    state: loadingAsyncState(() => loadFormstackForm(p.field_formstack_form_id))
  })
}

export const panoramaBlock: (p: ParsedParagraphPanorama) => PanoramaBlockProps = p => {
  console.log('p', p);
  
  return ({
    kind: "panorama",
    imageUrl: fixMediaUrl(p.field_image.field_media_image.image.url),
  })
}


export const emptyBlock: (block: { bundle: string }) => Block = (block) => {
  console.warn("Unable to convert block:", block.bundle)
  return ({ kind: "empty" })
}
